import { useCallback, useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { money } from "@/components/status";
import { Button } from "@/components/ui/button";
import { RefreshCw } from "lucide-react";
import { toast } from "sonner";

function PayoutModeBadge({ payout }) {
  if (payout.connect_pending) {
    return <span className="text-xs font-semibold px-2 py-0.5 rounded bg-amber-100 text-amber-800">Pending transfer</span>;
  }
  if (payout.mode === "live") {
    return <span className="text-xs font-semibold px-2 py-0.5 rounded bg-emerald-100 text-emerald-800">Stripe transfer</span>;
  }
  return <span className="text-xs font-semibold px-2 py-0.5 rounded bg-slate-100 text-slate-600">Simulated</span>;
}

export default function AdminPayouts() {
  const [payouts, setPayouts] = useState([]);
  const [busy, setBusy] = useState(null);
  const load = useCallback(() => api.get("/admin/payouts").then((r) => setPayouts(r.data)), []);
  useEffect(() => { load(); }, [load]);
  const total = payouts.filter((p) => p.status === "paid").reduce((s, p) => s + (p.net_to_cleaner ?? p.amount), 0);

  const retry = async (id) => {
    setBusy(id);
    try {
      const { data } = await api.post(`/admin/payouts/${id}/retry`);
      toast.success(data.transfer_id ? `Real transfer sent (${data.transfer_id.slice(0, 12)}…)` : data.message);
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  return (
    <div className="space-y-6" data-testid="admin-payouts">
      <div>
        <h1 className="font-heading font-black text-3xl tracking-tight text-sapphire">Payout Logs</h1>
        <p className="text-muted-foreground text-sm mt-1">Total to cleaners: <span className="font-semibold text-sapphire">{money(total)}</span></p>
      </div>
      <div className="border border-border rounded-md bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-border">
            <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground">
              <th className="px-4 py-3">Cleaner</th><th className="px-4 py-3">Net Paid</th>
              <th className="px-4 py-3">Platform Fee</th><th className="px-4 py-3">Settlement</th>
              <th className="px-4 py-3">Transfer ID</th><th className="px-4 py-3">Date</th><th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {payouts.map((p) => (
              <tr key={p.id} data-testid={`admin-payout-${p.id}`}>
                <td className="px-4 py-3 font-semibold text-sapphire">{p.cleaner?.name || p.cleaner_id}</td>
                <td className="px-4 py-3">{money(p.net_to_cleaner ?? p.amount)}</td>
                <td className="px-4 py-3 text-ocean font-semibold">{money(p.platform_fee)}</td>
                <td className="px-4 py-3">
                  <PayoutModeBadge payout={p} />
                </td>
                <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{p.stripe_transfer_id || "—"}</td>
                <td className="px-4 py-3 text-xs text-muted-foreground">{new Date(p.created_at).toLocaleString()}</td>
                <td className="px-4 py-3">
                  {p.connect_pending && (
                    <Button size="sm" variant="outline" disabled={busy === p.id} onClick={() => retry(p.id)} data-testid={`retry-payout-${p.id}`}>
                      <RefreshCw className={`w-3.5 h-3.5 mr-1 ${busy === p.id ? "animate-spin" : ""}`} /> Retry
                    </Button>
                  )}
                </td>
              </tr>
            ))}
            {payouts.length === 0 && <tr><td colSpan={7} className="px-4 py-8 text-center text-muted-foreground">No payouts yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
